import { useMemo } from 'react';

import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import Card from '@/entities/Card';
import Button from '@/shared/ui/Button';
import Table, { DateCell, TextCell } from '@/shared/ui/Table';
import { openNewTab } from '@/shared/utils/openLink'
import { IconList } from '@/shared/ui/Icons';
import StatusIcon from '@/shared/ui/StatusIcon';
import NewTabLink from '@/shared/ui/NewTabLink';
import { 
  ITEMS_IN_WIDGET, 
  SORT_BY,
  getStatusIconVariant,
} from '~/accountingAndAnonymous/Payouts';
import { useGetBalanceIdQuery, useGetTransactionsQuery } from '@/shared/api/Payouts';
import { selectPollingInterval } from "@/processes/Settings";
import { useNavigation, VIEWS } from '@/processes/Router';
import { useExplorer } from '@/processes/PoolStats';
import { useMinerAccount } from "@/processes/MinerAccounts";
import { ETC_SLICE_VALUE } from '@/shared/constants/payouts';

type PayoutsWidgetProps = {
  className: string;
}

const PayoutsWidget = (props: PayoutsWidgetProps) => {
  const { className } = props;
  const intl = useIntl();
  const pollingInterval = useSelector(selectPollingInterval);
  const { coin, updateURLQueryParams, isLTCBTC } = useNavigation();
  const { account, miner } = useMinerAccount();
  const { explorerLink } = useExplorer({ coin });

  const { 
    data: balanceId,
  } = useGetBalanceIdQuery({ account, coin, miner }, { skip: !(account || miner) })
  const { 
    data: transactions = [],
  } = useGetTransactionsQuery(
    {
      coin,
      balanceId,
      account,
      miner,
      limit: ITEMS_IN_WIDGET,
      isSortingDESC: true,
      orderBy: SORT_BY.CREATED_AT,
      offset: 0,
    },
    {
      pollingInterval,
      skip: !coin,
    }
  )
  const isTransactionsEmpty = transactions.length === 0;
  const transactionsPreview = useMemo(() => transactions.slice(0, ITEMS_IN_WIDGET), [transactions]);

  const slicedValue = (value: string) => value.slice(0, ETC_SLICE_VALUE);

  return (
    <Card 
      className={className} 
      title={intl.formatMessage({ id: 'payouts.history' })}
      actions={
        !isTransactionsEmpty ? (
          <Button 
          isOverlay={true}
          onClick={() => updateURLQueryParams({ view: VIEWS.PAYOUTS })}
          prefixIcon={(<IconList/>)}
        >{intl.formatMessage({ id: 'details' })}</Button>
        ) : null
      }
    >
      <Table
        classNameRoot='mt-6'
        stripedRows={true}
        withHeader={false}
        data={transactionsPreview}
        onRowClick={(row) => {
          row.txHash && openNewTab(explorerLink(row.txHash));
        }}
        columns={[
          {
            Header: 'Date',
            accessor: 'createdAt',
            disableSortBy: true,
            Cell: DateCell,
          }, 
          {
            Header: 'Amount',
            accessor: 'value',
            disableSortBy: true,
            Cell: ({ value }: any) => <TextCell value={slicedValue(value)} subValue={isLTCBTC ? 'BTC' : coin}/>
          }, 
          {
            Header: 'Actions',
            accessor: 'status',
            className: 'w-[70px]',
            disableSortBy: true,
            Cell: (props: any) => {
              const { value, row, data } = props;
              const hash = _get(data, `${row.index}.txHash`, null);
              return (
                <span className='flex items-center justify-end'>
                  <StatusIcon
                    className='w-[14px] h-[14px]'
                    status={getStatusIconVariant(value)}
                  />
                  <div className="ml-2 w-6 inline-flex items-center">
                    {hash ? (
                      <NewTabLink/>
                    ) : null}
                  </div>
                </span>
              )
            }
          }
        ]}
      />
    </Card>
  );
}

export default PayoutsWidget;
